;(() => {
  const namespace = (globalThis.StylistLocator = globalThis.StylistLocator || {})

  // ============================================================================
  // Dependencies
  // ============================================================================

  const { templates = {}, utils = {} } = namespace
  const { buildGalleryMarkup = () => '' } = templates
  const { escapeHtml = (v) => (v == null ? '' : String(v)), encodeKey = (v) => v } = utils

  const DEFAULT_ROLE_LABEL = 'IBE® Stylist'
  const DEFAULT_STYLIST_NAME = 'Unnamed Stylist'

  // ============================================================================
  // Read More Functionality
  // ============================================================================

  /**
   * Initialize "read more" buttons for description text.
   * @param {HTMLElement} scope - Container to search for buttons
   */
  function initializeReadMore(scope) {
    if (!scope) return

    scope.querySelectorAll('[data-read-more]').forEach((button) => {
      const wrapper = button.closest('.stylist-locator__info-description-wrapper') || scope
      const description = wrapper.querySelector('[data-description-text]')

      if (!description) {
        button.remove()
        return
      }

      const isOverflowing = description.scrollHeight - description.clientHeight > 1
      if (!isOverflowing) {
        button.remove()
        description.classList.remove('stylist-locator__info-description--expanded')
        return
      }

      button.addEventListener('click', () => {
        const isExpanded = description.classList.toggle('stylist-locator__info-description--expanded')
        button.textContent = isExpanded ? 'read less' : 'read more'
      })
    })
  }

  // ============================================================================
  // Profile Sections
  // ============================================================================

  /**
   * Generate profile section data for a stylist.
   * @param {Object} stylist - Stylist object
   * @returns {Object} Profile sections with escaped HTML
   */
  function getProfileSections(stylist) {
    const safeName = escapeHtml(stylist.name || DEFAULT_STYLIST_NAME)
    // If stylist is a certified salon without a salon name, display "Certified Salon" as role
    const isCertifiedSalonWithoutName = (stylist.isCertifiedSalon === true || stylist.isSalonOwner === true) && !stylist.salonName
    const roleText = isCertifiedSalonWithoutName ? 'Certified Salon' : (stylist.role || DEFAULT_ROLE_LABEL)
    const safeRole = escapeHtml(roleText)
    const safeSalonName = stylist.salonName ? escapeHtml(stylist.salonName) : ''
    const safeAddress = stylist.address ? escapeHtml(stylist.address) : ''

    const avatarMarkup = buildAvatarMarkup(stylist, safeName)
    const descriptionMarkup = buildDescriptionMarkup(stylist.description)
    const tagsMarkup = buildTagsMarkup(stylist)

    return {
      name: safeName,
      role: safeRole,
      salonName: safeSalonName,
      address: safeAddress,
      avatar: avatarMarkup,
      description: descriptionMarkup,
      tags: tagsMarkup,
      gallery: buildGalleryMarkup(stylist),
    }
  }

  /**
   * Render a single tag badge.
   */
  function renderTagMarkup(label, value) {
    if (!value) return ''
    return `<span class="stylist-locator__tag stylist-locator__tag--primary">${escapeHtml(label)}</span>`
  }

  function getInitials(name) {
    return (name || '')
      .split(' ')
      .filter(Boolean)
      .map((part) => part[0])
      .join('')
      .slice(0, 2)
  }

  function buildAvatarMarkup(stylist, safeName) {
    if (stylist.profile_photo) {
      const avatarSrc = escapeHtml(stylist.profile_photo)
      return `<img src="${avatarSrc}" alt="${safeName}">`
    }

    const initials = getInitials(stylist.name)
    return `<span>${initials || 'IB'}</span>`
  }

  function buildDescriptionMarkup(description) {
    if (!description) return ''
    const descriptionText = escapeHtml(description)
    return `
      <div class="stylist-locator__info-description-wrapper">
        <p class="stylist-locator__info-description" data-description-text>${descriptionText}</p>
        <button type="button" class="stylist-locator__info-read-more" data-read-more>read more</button>
      </div>
    `
  }

  function buildTagsMarkup(stylist) {
    const tagBadges = [
      renderTagMarkup('Business', stylist.business),
      renderTagMarkup('Cutting', stylist.cutting),
      renderTagMarkup('Color', stylist.color),
      renderTagMarkup('Technique', stylist.technique),
    ].filter(Boolean)

    if (!tagBadges.length) {
      return ''
    }

    return `
      <div class="stylist-locator__info-tags-section">
        <p class="stylist-locator__info-tags-title">Graduate of Masters</p>
        <div class="stylist-locator__info-tags">${tagBadges.join('')}</div>
      </div>
    `
  }

  // ============================================================================
  // Info Window Content
  // ============================================================================

  /**
   * Render info window content for a stylist marker.
   * @param {Object} stylist - Stylist object
   * @param {Object} state - Application state
   * @param {Object} callbacks - Callbacks including getProfileSections
   * @returns {string} HTML content for info window
   */
  function renderInfoWindowContent(stylist, state, callbacks) {
    const { getProfileSections: getSections = getProfileSections } = callbacks
    const sections = getSections(stylist)
    const showForm = state.showRequest && state.selectedStylist?.email === stylist.email
    const emailAttr = escapeHtml(encodeKey(stylist.email || ''))

    const backButton = showForm
      ? '<button type="button" class="stylist-locator__drawer-back" data-info-back aria-label="Go back"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M16 4L8 12L16 20" stroke="black" stroke-opacity="0.6" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg></button>'
      : ''

    const closeButton = `
      <button type="button" class="stylist-locator__info-close" data-info-close aria-label="Close">
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M13 1L1 13M1 1L13 13" stroke="black" stroke-opacity="0.6" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
    `

    const formMarkup = buildRequestFormMarkup({
      name: state.name,
      email: state.email,
      phone: state.phone,
      howDidYouHear: state.howDidYouHear,
      namespace: 'info',
    })

    const addressMarkup = sections.address ? `<p class="stylist-locator__info-address">${sections.address}</p>` : ''
    const bodyMarkup = showForm
      ? formMarkup
      : `${sections.description}${addressMarkup}${sections.tags}${sections.gallery}`

    const headerContent = showForm
      ? `
        <div class="stylist-locator__info-meta">
          <p class="stylist-locator__info-name">${sections.name}</p>
          ${sections.salonName ? `<p class="stylist-locator__info-salon">${sections.salonName}</p>` : ''}
          <p class="stylist-locator__info-role">${sections.role}</p>
        </div>
      `
      : `
        <div class="stylist-locator__info-header-content">
          <div class="stylist-locator__info-avatar">${sections.avatar}</div>
          <div class="stylist-locator__info-meta">
            <p class="stylist-locator__info-name">${sections.name}</p>
            ${sections.salonName ? `<p class="stylist-locator__info-salon">${sections.salonName}</p>` : ''}
            <p class="stylist-locator__info-role">${sections.role}</p>
          </div>
        </div>
      `

    return `
      <div class="stylist-locator__info-wrapper">
        <div class="stylist-locator__info-card" data-info-root="${emailAttr}">
          <div class="stylist-locator__info-header">
            ${backButton}
            ${headerContent}
            ${closeButton}
          </div>
          ${bodyMarkup}
          <button type="button" class="stylist-locator__button stylist-locator__button--full" data-info-request-button>
            Request Appointment
          </button>
        </div>
        <div class="stylist-locator__info-tooltip">
          <svg xmlns="http://www.w3.org/2000/svg" width="60" height="20" viewBox="0 0 60 20" fill="none">
            <path d="M28.7612 18.6278C25.6193 14.0446 14.9617 0.0639965 1.86375 0.0639685C-13.8019 0.0639351 74.9254 -0.166599 57.6498 0.247857C43.2838 0.592508 33.4857 14.0669 30.5928 18.596C30.179 19.2438 29.1958 19.2618 28.7612 18.6278Z" fill="#F9F8F2"/>
          </svg>
        </div>
      </div>
    `
  }

  // ============================================================================
  // Drawer Content
  // ============================================================================

  /**
   * Render drawer content for mobile view.
   * @param {Object} stylist - Stylist object
   * @param {Object} state - Application state
   * @param {Object} callbacks - Callbacks including getProfileSections
   * @returns {string} HTML content for drawer
   */
  function renderDrawerContent(stylist, state, callbacks) {
    if (!stylist) return ''

    const { getProfileSections: getSections = getProfileSections } = callbacks
    const sections = getSections(stylist)
    const showForm = state.showRequest

    const closeButton = `
      <button class="stylist-locator__drawer-close" type="button" data-element="drawer-close" aria-label="Close drawer">
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <mask id="mask-drawer-close" style="mask-type:alpha" maskUnits="userSpaceOnUse" x="0" y="0" width="24" height="24">
            <rect width="24" height="24" fill="#D9D9D9"/>
          </mask>
          <g mask="url(#mask-drawer-close)">
            <path d="M18 6L6 18M6 6L18 18" stroke="black" stroke-opacity="0.6" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </g>
        </svg>
      </button>
    `

    const backButton = showForm
      ? '<button class="stylist-locator__drawer-back" data-element="drawer-back" aria-label="Go back"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M16 4L8 12L16 20" stroke="black" stroke-opacity="0.6" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg></button>'
      : ''

    const formMarkup = buildRequestFormMarkup({
      name: state.name,
      email: state.email,
      phone: state.phone,
      howDidYouHear: state.howDidYouHear,
      namespace: 'drawer',
    })

    const addressMarkup = sections.address ? `<p class="stylist-locator__info-address">${sections.address}</p>` : ''
    const bodyMarkup = showForm
      ? formMarkup
      : `${sections.description}${addressMarkup}${sections.tags}${sections.gallery}`

    const headerContent = showForm
      ? `
        <div class="stylist-locator__drawer-header-content">
          ${backButton}
          <div class="stylist-locator__info-meta">
            <p class="stylist-locator__info-name">${sections.name}</p>
            ${sections.salonName ? `<p class="stylist-locator__info-salon">${sections.salonName}</p>` : ''}
            <p class="stylist-locator__info-role">${sections.role}</p>
          </div>
        </div>
      `
      : `
        <div class="stylist-locator__drawer-header-content">
          <div class="stylist-locator__drawer-avatar-wrapper">
            <div class="stylist-locator__info-avatar stylist-locator__info-avatar--drawer">${sections.avatar}</div>
            <div class="stylist-locator__info-meta">
              <p class="stylist-locator__info-name">${sections.name}</p>
              ${sections.salonName ? `<p class="stylist-locator__info-salon">${sections.salonName}</p>` : ''}
              <p class="stylist-locator__info-role">${sections.role}</p>
            </div>
          </div>
        </div>
      `

    return `
      <div class="stylist-locator__drawer-header">
        ${headerContent}
        ${closeButton}
      </div>
      <div class="stylist-locator__drawer-body">
        ${bodyMarkup}
        <button class="stylist-locator__button stylist-locator__button--full" data-element="request-button">
          Request Appointment
        </button>
      </div>
    `
  }

  function buildRequestFormMarkup({ name = '', email = '', phone = '', howDidYouHear = '', namespace }) {
    if (namespace === 'info') {
      return `
        <form class="stylist-locator__form" data-info-form>
          <input type="text" placeholder="Name *" data-info-request-name value="${escapeHtml(name)}" required>
          <input type="email" placeholder="Email *" data-info-request-email value="${escapeHtml(email)}" required>
          <input type="tel" placeholder="Phone *" data-info-request-phone value="${escapeHtml(phone)}" required>
          <textarea placeholder="How did you hear about us? *" data-info-request-how-did-you-hear rows="3" required>${escapeHtml(howDidYouHear)}</textarea>
        </form>
      `
    }

    return `
      <form class="stylist-locator__form">
        <input type="text" placeholder="Name *" data-element="request-name" value="${escapeHtml(name)}" required>
        <input type="email" placeholder="Email *" data-element="request-email" value="${escapeHtml(email)}" required>
        <input type="tel" placeholder="Phone *" data-element="request-phone" value="${escapeHtml(phone)}" required>
        <textarea placeholder="How did you hear about us? *" data-element="request-how-did-you-hear" rows="3" required>${escapeHtml(howDidYouHear)}</textarea>
      </form>
    `
  }

  // ============================================================================
  // Exports
  // ============================================================================

  namespace.info = Object.assign({}, namespace.info, {
    initializeReadMore,
    getProfileSections,
    renderTagMarkup,
    renderInfoWindowContent,
    renderDrawerContent,
  })
})()
